import PropTypes from 'prop-types';
import classNames from 'classnames';

import './ModalAction.scss';

// The ring is drawn in the button's own 32px box, a hair inside its edge so the stroke doesn't clip
const RING_SIZE = 32;
const RING_RADIUS = 14.5;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * The wait, drawn around the glyph: an arc that fills where the amount is known, and a short one
 * spinning where it isn't. Sized in the viewBox rather than in pixels, so it follows the button if
 * the button is ever sized differently.
 */
const ProgressRing = ({ progress }) => (
  <svg className="progress-ring" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
    <circle className="track" cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} />
    <circle
      className={progress == null ? 'arc spinning' : 'arc'}
      cx={RING_SIZE / 2}
      cy={RING_SIZE / 2}
      r={RING_RADIUS}
      strokeDasharray={RING_LENGTH}
      // A quarter turn of arc is what spins; a known amount is that fraction of the circle
      strokeDashoffset={progress == null ? RING_LENGTH * 0.75 : RING_LENGTH * (1 - progress)}
    />
  </svg>
);

ProgressRing.propTypes = {
  progress: PropTypes.number,
};

/**
 * A button in a modal's header, beside the X — the same grey circle, with an icon of its own.
 *
 * For the one thing a long dialog shouldn't make you scroll back to: playing the chart from the
 * top of the player, say, where the transport is under a chart taller than the screen. It carries
 * no text, so `label` is both its accessible name and its tooltip, and it is on the caller to pick
 * a glyph that needs no caption.
 *
 * `active` is for a button that toggles between two states — pass the state's own icon with it, the
 * way play becomes stop, and the circle takes the accent to say it is doing something.
 *
 * A filled glyph goes in with `className="solid"`, the same mark App.scss reads on `.icon`, and
 * `small` takes a step off one that fills its box — a solid square against a play triangle.
 *
 * `busy` rings the button while whatever it does is being got ready — sound downloading, a clip
 * decoding. It is the visible half of a disabled state that would otherwise only be a dimmed
 * button: `label` says what is happening, but a header button has nowhere to put the words.
 */
const ModalAction = ({
  icon,
  label,
  onClick,
  disabled = false,
  active = false,
  busy = false,
  progress = null,
  className,
}) => (
  <button
    type="button"
    className={classNames('ModalAction', { active, busy }, className)}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    aria-busy={busy || undefined}
  >
    {icon}
    {busy && <ProgressRing progress={progress} />}
  </button>
);

ModalAction.propTypes = {
  /** The glyph, as an imported SVG element */
  icon: PropTypes.node.isRequired,
  /** What it does, in a couple of words — the tooltip and the accessible name both */
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** Whether the thing it controls is currently on */
  active: PropTypes.bool,
  /** Whether it is waiting on something before it can be pressed — draws the ring */
  busy: PropTypes.bool,
  /** How far along that wait is, 0–1. Null where it can't be counted, and the ring spins instead */
  progress: PropTypes.number,
  className: PropTypes.string,
};

export default ModalAction;
